import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient, { formatApiError } from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import * as PhosphorIcons from '@phosphor-icons/react';

const { Storefront, MusicNotes, Queue, Clock, Broadcast, CheckCircle, ArrowRight, ArrowLeft, Confetti } = PhosphorIcons;

const steps = [
  { id: 'stores', title: 'Create a Store', desc: 'Add your first retail location', icon: Storefront },
  { id: 'drive', title: 'Connect Google Drive', desc: 'Link your music library from Drive', icon: MusicNotes },
  { id: 'playlist', title: 'Create a Playlist', desc: 'Build your first playlist', icon: Queue },
  { id: 'daypart', title: 'Set Up Dayparting', desc: 'Schedule music for different times', icon: Clock },
  { id: 'kiosk', title: 'Deploy to Kiosk', desc: 'Open kiosk mode in your store', icon: Broadcast },
];

const SetupWizard = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [completed, setCompleted] = useState({});
  const [loading, setLoading] = useState(true);
  const [storeForm, setStoreForm] = useState({ name: '', location: '' });
  const [stores, setStores] = useState([]);
  const [driveStatus, setDriveStatus] = useState({ connected: false });

  useEffect(() => { checkProgress(); }, []);

  const checkProgress = async () => {
    try {
      const [storesRes, driveRes] = await Promise.all([
        apiClient.get('/stores'),
        apiClient.get('/drive/status').catch(() => ({ data: { connected: false } }))
      ]);
      setStores(storesRes.data);
      setDriveStatus(driveRes.data);

      const c = {};
      if (storesRes.data.length > 0) c.stores = true;
      if (driveRes.data.connected) c.drive = true;

      const playlistsRes = await apiClient.get('/playlists');
      if (playlistsRes.data.length > 0) c.playlist = true;

      const daypartsRes = await apiClient.get('/dayparts');
      if (daypartsRes.data.length > 0) c.daypart = true;

      setCompleted(c);

      // Jump to first incomplete step
      const firstIncomplete = steps.findIndex(s => !c[s.id]);
      if (firstIncomplete >= 0) setCurrent(firstIncomplete);
    } catch (e) { console.error('[setup] fetch progress failed', e); }
    finally { setLoading(false); }
  };

  const handleCreateStore = async () => {
    if (!storeForm.name || !storeForm.location) { toast.error('Fill in store name and location'); return; }
    try {
      await apiClient.post('/stores', storeForm);
      toast.success('Store created!');
      setCompleted(prev => ({ ...prev, stores: true }));
      setStoreForm({ name: '', location: '' });
      setCurrent(1);
      checkProgress();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const handleConnectDrive = async () => {
    try {
      const res = await apiClient.get('/drive/connect');
      if (res.data.authorization_url) {
        window.location.href = res.data.authorization_url;
      }
    } catch (error) {
      toast.error('Google Drive not configured yet. Please ask the admin to set up Google OAuth credentials.');
    }
  };

  const allDone = Object.keys(completed).length >= 4;

  if (loading) return <div className="text-[#6b665c] font-mono p-8">Loading setup...</div>;

  return (
    <div className="max-w-3xl mx-auto" data-testid="setup-wizard">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight mb-2">Setup Guide</h1>
        <p className="text-[#6b665c] font-mono text-sm uppercase tracking-wider">Get your music system running in 5 steps</p>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-1 mb-8">
        {steps.map((step, i) => (
          <div key={step.id} className="flex items-center flex-1">
            <button
              onClick={() => setCurrent(i)}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-mono transition-all ${
                completed[step.id] ? 'bg-green-600 text-white' : i === current ? 'bg-blue-600 text-white' : 'bg-[#e4ebe5] text-[#8a8477]'
              }`}
              data-testid={`step-indicator-${step.id}`}
            >
              {completed[step.id] ? <CheckCircle size={16} weight="fill" /> : i + 1}
            </button>
            {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${completed[step.id] ? 'bg-green-600' : 'bg-[#e4ebe5]'}`} />}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="bg-white border border-[#c5d4c8] rounded-xl p-8">
        {(() => {
          const step = steps[current];
          const Icon = step.icon;
          const isDone = completed[step.id];

          return (
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${isDone ? 'bg-green-900/30 border border-green-800/40' : 'bg-blue-900/30 border border-[#c5d4c8]'}`}>
                  <Icon size={28} className={isDone ? 'text-green-400' : 'text-[#7c9082]'} />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{step.title}</h2>
                  <p className="text-sm text-[#6b665c]">{step.desc}</p>
                </div>
                {isDone && <span className="ml-auto text-green-400 text-sm font-mono flex items-center gap-1"><CheckCircle size={16} weight="fill" /> Done</span>}
              </div>

              {/* Step-specific content */}
              {step.id === 'stores' && (
                <div className="space-y-4">
                  {isDone ? (
                    <p className="text-[#6b665c] text-sm">You have {stores.length} store(s) configured. You can add more from the Stores page.</p>
                  ) : (
                    <>
                      <div>
                        <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Store Name</Label>
                        <Input value={storeForm.name} onChange={(e) => setStoreForm({...storeForm, name: e.target.value})} className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" placeholder="e.g., Downtown Branch" data-testid="wizard-store-name" />
                      </div>
                      <div>
                        <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Location</Label>
                        <Input value={storeForm.location} onChange={(e) => setStoreForm({...storeForm, location: e.target.value})} className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" placeholder="e.g., 123 Main St" data-testid="wizard-store-location" />
                      </div>
                      <Button onClick={handleCreateStore} className="bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="wizard-create-store-btn">Create Store</Button>
                    </>
                  )}
                </div>
              )}

              {step.id === 'drive' && (
                <div className="space-y-4">
                  {driveStatus.connected ? (
                    <p className="text-green-400 text-sm font-mono">Google Drive is connected. Browse files from the Music Library page.</p>
                  ) : (
                    <>
                      <p className="text-[#6b665c] text-sm">Connect your Google Drive to browse and play audio files from your Drive directly in your stores.</p>
                      <Button onClick={handleConnectDrive} className="bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="wizard-connect-drive-btn">Connect Google Drive</Button>
                    </>
                  )}
                </div>
              )}

              {step.id === 'playlist' && (
                <div className="space-y-4">
                  {isDone ? (
                    <p className="text-[#6b665c] text-sm">Playlists are ready. Manage them from the Playlists page.</p>
                  ) : (
                    <>
                      <p className="text-[#6b665c] text-sm">Create your first playlist by selecting tracks from your Google Drive library.</p>
                      <Button onClick={() => navigate('/dashboard/playlists')} className="bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="wizard-go-playlists-btn">Go to Playlists</Button>
                    </>
                  )}
                </div>
              )}

              {step.id === 'daypart' && (
                <div className="space-y-4">
                  {isDone ? (
                    <p className="text-[#6b665c] text-sm">Dayparting is configured. Manage schedules from the Dayparting page.</p>
                  ) : (
                    <>
                      <p className="text-[#6b665c] text-sm">Set up time-based scheduling so different playlists play at different times of day.</p>
                      <Button onClick={() => navigate('/dashboard/dayparting')} className="bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="wizard-go-dayparting-btn">Go to Dayparting</Button>
                    </>
                  )}
                </div>
              )}

              {step.id === 'kiosk' && (
                <div className="space-y-4">
                  <p className="text-[#6b665c] text-sm">Create a kiosk user for each store, then log in from a dedicated device at that location. The kiosk will start playing automatically.</p>
                  <div className="bg-[#f5f0e8] border border-[#c5d4c8] rounded-lg p-4 text-sm text-[#6b665c] space-y-2">
                    <p>1. Go to <strong>Users</strong> page and create a user with <strong>Kiosk</strong> role</p>
                    <p>2. Assign the user to a specific store</p>
                    <p>3. On the store device, log in with those credentials</p>
                    <p>4. The kiosk will auto-launch and start playing the scheduled music</p>
                  </div>
                  <Button onClick={() => navigate('/dashboard/users')} className="bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="wizard-go-users-btn">Go to Users</Button>
                </div>
              )}

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#c5d4c8]">
                <Button onClick={() => setCurrent(Math.max(0, current - 1))} disabled={current === 0} className="bg-[#e4ebe5] hover:bg-[#d9d3c5] text-[#5a6e5f] border border-[#c5d4c8] disabled:opacity-30">
                  <ArrowLeft size={16} className="mr-2" />Previous
                </Button>
                {current < steps.length - 1 ? (
                  <Button onClick={() => setCurrent(current + 1)} className="bg-blue-600 hover:bg-blue-700 text-white border-0">
                    Next<ArrowRight size={16} className="ml-2" />
                  </Button>
                ) : allDone ? (
                  <Button
                    onClick={async () => {
                      try { await apiClient.patch('/tenant/settings', { setup_completed: true }); }
                      catch (e) { /* non-fatal */ }
                      window.location.href = '/dashboard/';
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white border-0"
                    data-testid="wizard-finish-btn"
                  >
                    <Confetti size={16} className="mr-2" />Finish &amp; Go to Dashboard
                  </Button>
                ) : null}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default SetupWizard;
